import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { IgButton, IgTypography } from "@intelligems/ig-theme";
import { ExperienceDto } from "@intelligems/ig-types";
import { Dialog, DialogActions, DialogTitle, Grid } from "@mui/material";
import { styled } from "@mui/material/styles";

import { useFileBuild } from "../../../../api/experience-files/file-build";
import { getVariationDtoFromFe } from "../../../../api/formatters/from-dto/experiences/variations";
import { getDialog, setDialog } from "../../../../store/slices/global";
import { selectedVariations } from "../../../../store/slices/variations";
import { ExperienceFe } from "../../../../types/schemas/experiences";
import { VariationFe } from "../../../../types/schemas/variations";
import { multiCurrencyTesting } from "../../../../utils/linkSheet";
import openNewTab from "../../../../utils/openNewTab";
import { VariationButtons } from "../VariationButtons";

const PREFIX = "MarketPriceUpdaterDialog";

const classes = {
  root: `${PREFIX}-root`,
  dialogTitle: `${PREFIX}-dialogTitle`,
  dialogText1: `${PREFIX}-dialogText1`,
  dialogText2: `${PREFIX}-dialogText2`,
  articleLink: `${PREFIX}-articleLink`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.root}`]: {
    transition: "0.2s",
    borderRadius: "4px",
    border: `1px solid ${theme.palette.gray.light}`,
    "&:hover, &:focus": {
      border: `1px solid ${theme.palette.primary.main}`,
    },
  },
  [`& .${classes.dialogTitle}`]: {
    fontSize: "22px",
    fontWeight: 600,
  },
  [`& .${classes.dialogText1}`]: {
    marginTop: "15px",
    marginBottom: "15px",
    fontSize: "18px",
    color: theme.palette.grey[900],
  },
  [`& .${classes.dialogText2}`]: {
    fontSize: "18px",
    marginBottom: 15,
  },
  [`& .${classes.articleLink}`]: {
    textDecoration: "none",
    fontSize: "18px",
    color: theme.palette.primary.main,
    "&:hover": {
      color: theme.palette.primary.dark,
    },
    cursor: "pointer",
  },
}));

interface MarketPriceUpdaterDialogProps {
  currExperience: ExperienceDto;
}

export const MarketPriceUpdaterDialog = ({
  currExperience,
}: MarketPriceUpdaterDialogProps) => {
  const dispatch = useDispatch();
  const variations = useSelector(selectedVariations);
  const marketPriceUpdater = useSelector(getDialog("marketPriceUpdater"));

  const buildPriceCsvFile = useFileBuild();

  const [selectedTestVariation, setSelectedTestVariation] = useState<
    string | undefined
  >(undefined);

  const selectedCard = (id: string): boolean => {
    return selectedTestVariation === id;
  };

  const closeDialog = () => {
    dispatch(
      setDialog({
        dialog: "marketPriceUpdater",
        open: false,
        props: { action: "none" },
      })
    );
  };

  const continueAction = () => {
    dispatch(
      setDialog({
        dialog: "marketUpdateConfirm",
        open: true,
        props: {
          action: marketPriceUpdater.props.action,
          testGroupId: selectedTestVariation,
        },
      })
    );
    closeDialog();
  };

  const onDownloadClick = () => {
    const currentTestGroup = variations.find(
      (tg) => tg.id === selectedTestVariation
    );
    let tempVariations: VariationFe[] = [];
    if (currentTestGroup) tempVariations = [currentTestGroup];

    buildPriceCsvFile.mutate({
      body: {
        variations: getVariationDtoFromFe(tempVariations),
        selectedProducts: [],
        name: currExperience.name,
        experienceId: currExperience.id!,
        forExternalPriceUpload: true,
      },
      pathParams: {
        type: "csv",
      },
      queryParams: undefined,
    });
  };

  return marketPriceUpdater.isOpen ? (
    <StyledDialog
      open={marketPriceUpdater.isOpen}
      aria-labelledby="market-pricing-dialog"
      aria-describedby="market-pricing-dialog"
      onClose={() => {
        dispatch(
          setDialog({
            dialog: "marketPriceUpdater",
            open: false,
            props: { action: "none" },
          })
        );
      }}
      maxWidth={"xs"}
      fullWidth={true}
      PaperProps={{
        style: { borderRadius: "15px", padding: "30px 20px 30px 20px" },
      }}
    >
      <Grid
        container
        direction={"column"}
        alignItems={"center"}
        textAlign={"center"}
      >
        <DialogTitle
          sx={{ width: "350px" }}
          id="dialogBox-title"
          className={classes.dialogTitle}
        >
          Upload Prices to your Market
        </DialogTitle>
        <DialogTitle
          sx={{ width: "350px" }}
          id="dialogBox-body"
          className={classes.dialogText2}
        >
          {marketPriceUpdater.props.action === "start"
            ? "In order for your test prices to adjust correctly, you'll need to set prices to the highest priced test group. Download the CSV and upload your prices to Shopify Markets or Global-e before starting your test."
            : "Choose the test group that you'd like to roll out, then use the CSV to upload prices in Shopify Markets or Global-e before continuing."}{" "}
          Learn more{" "}
          <IgTypography
            className={classes.articleLink}
            onClick={() => {
              openNewTab(multiCurrencyTesting);
            }}
          >
            here.
          </IgTypography>{" "}
        </DialogTitle>
        {marketPriceUpdater.props.action !== "start" && (
          <VariationButtons
            selectedTestGroup={selectedTestVariation}
            setSelectedTestGroup={setSelectedTestVariation}
            selectedCard={selectedCard}
            rootClass={classes.root}
            variations={currExperience.variations}
            sx={{
              marginBottom: "15px",
            }}
          />
        )}
      </Grid>
      <DialogActions>
        <Grid container direction={"column"}>
          <IgButton
            loading={buildPriceCsvFile.isPending}
            variant={"contained"}
            onClick={onDownloadClick}
            disabled={
              !selectedTestVariation &&
              marketPriceUpdater.props.action !== "start"
            }
          >
            {`Download CSV`}
          </IgButton>
          <IgButton
            onClick={continueAction}
            // disabled={!buildPriceCsvFile.isSuccess}
          >
            {"I've downloaded the CSV, continue"}
          </IgButton>
          <IgButton loading={false} onClick={closeDialog}>
            Cancel
          </IgButton>
        </Grid>
      </DialogActions>
    </StyledDialog>
  ) : null;
};
